import { useCallback, useEffect, useSyncExternalStore } from 'react';

const dispatchStorageEvent = (key: string, newValue: string | null) => {
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
};

const setSessionStorageItem = <T>(key: string, value: T) => {
  const stringifiedValue = JSON.stringify(value);
  window.sessionStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
};

const removeSessionStorageItem = (key: string) => {
  window.sessionStorage.removeItem(key);
  dispatchStorageEvent(key, null);
};

const getSessionStorageItem = (key: string): string | null => {
  return window.sessionStorage.getItem(key);
};

const useSessionStorageSubscribe = (callback: () => void): (() => void) => {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
};

const getSessionStorageServerSnapshot = (): never => {
  throw Error('useSessionStorage is a client-only hook');
};

type Updater<T> = (prev: T) => T;
type SetStateValue<T> = T | Updater<T>;
type SetState<T> = (v: SetStateValue<T>) => void;

const isUpdater = <T>(v: SetStateValue<T>): v is Updater<T> =>
  typeof v === 'function';

export const useSessionStorage = <T>(
  key: string,
  initialValue: T,
): [T, SetState<T>] => {
  const getSnapshot = () => getSessionStorageItem(key);

  const store = useSyncExternalStore(
    useSessionStorageSubscribe,
    getSnapshot,
    getSessionStorageServerSnapshot,
  );

  const setState = useCallback<SetState<T>>(
    (v) => {
      try {
        const prev: T = store !== null ? JSON.parse(store) : initialValue;
        const nextState = isUpdater<T>(v) ? v(prev) : v;

        if (nextState === undefined || nextState === null) {
          removeSessionStorageItem(key);
        } else {
          setSessionStorageItem(key, nextState);
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [key, store, initialValue],
  );

  useEffect(() => {
    if (
      getSessionStorageItem(key) === null &&
      typeof initialValue !== 'undefined'
    ) {
      setSessionStorageItem(key, initialValue);
    }
  }, [key, initialValue]);

  const value: T = store !== null ? JSON.parse(store) : initialValue;
  return [value, setState];
};
